import { BookOpenTextIcon, CalendarCheckIcon, CalendarXIcon, ClipboardCheckIcon, DicesIcon, FileTextIcon, FolderSearchIcon, LayoutGridIcon, LightbulbIcon, PencilRulerIcon, ShapesIcon, ShuffleIcon } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { PRODUCT_IMAGE as BASE_PRODUCT_IMAGE, pageSamples as basePageSamples } from './content';

// RELIGIONE PRONTA™ (Italia). Fonte unica: CONTENIDO.md — promessa = consegna.
// IMMAGINI REALI: quando mockup e pagine del Tomo 1 sono su un URL https pubblico
// (es. Recursos di Impultienda), incollare qui gli URL. Finché sono vuoti si usano le immagini provvisorie.
export const REAL_MOCKUP_URL: string = '';
// Ordine: 1 lezione docente (p.12) · 2 scheda alunno Avvento (p.17) · 3 programmazione annuale (p.5) · 4 verifica (p.36) · 5 griglia di valutazione (p.39)
export const REAL_PAGE_URLS: string[] = [];
export const PRODUCT_IMAGE: string = REAL_MOCKUP_URL || BASE_PRODUCT_IMAGE;
export const PRODUCT_NAME = 'RELIGIONE PRONTA™';
export const heroGuarantees = ['Download digitale immediato', 'Pagamento unico', 'Nessun abbonamento', 'Garanzia di 7 giorni'];
export const stats = [{
  value: '240',
  label: 'Pagine nei 6 tomi principali'
}, {
  value: '6',
  label: 'Tomi digitali di IRC'
}, {
  value: '8',
  label: 'Classi: Primaria 1ª–5ª e Secondaria 1ª–3ª'
}, {
  value: '12',
  label: 'Lezioni minuto per minuto (60\') nel Tomo 1'
}, {
  value: '5',
  label: 'Bonus inclusi'
}];
export const differenceStats = [{
  value: 'Primaria e Secondaria',
  label: 'Programmazione annuale per ogni classe, dalla 1ª Primaria alla 3ª Secondaria'
}, {
  value: '6 tomi digitali',
  label: 'Dalla programmazione alla Bibbia, all\'anno liturgico, ai sacramenti, alle verifiche'
}, {
  value: 'PDF stampabili',
  label: 'Da usare in digitale o da stampare per la tua classe'
}];
export const beforeItems: Array<{
  title: string;
  text: string;
}> = [{
  title: 'La domenica sera sparisce',
  text: 'Ore davanti al computer a preparare le lezioni di religione della settimana – e non hai ancora finito.'
}, {
  title: 'Ore a cercare materiale',
  text: 'Schede e attività trovate su siti diversi: troppo facili, troppo difficili o da adattare prima di usarle.'
}, {
  title: 'Schede create da zero',
  text: 'Racconti biblici, domande, attività e soluzioni: quello che dovrebbe richiedere minuti diventa un lavoro lungo.'
}, {
  title: 'Difficile rendere l\'IRC visivo',
  text: 'Mappe, calendari liturgici e schede illustrate richiedono tempo per ogni nuovo argomento.'
}, {
  title: 'Verifiche e valutazione richiedono tempo',
  text: 'Preparare verifiche, griglie e giudizi per la scheda di valutazione diventa altro lavoro dopo la scuola.'
}, {
  title: 'Ansia prima della prossima lezione',
  text: 'Quando il tempo non basta, si finisce per improvvisare e mettere insieme il materiale all\'ultimo minuto.'
}];
export const afterItems = ['Un sistema di IRC organizzato per Primaria e Secondaria', 'Programmazione annuale per ogni classe', '12 lezioni minuto per minuto pronte da usare', 'Schede da stampare subito', 'Racconti dell\'Antico e del Nuovo Testamento con schede, giochi e mappe', 'Anno liturgico e feste già pronti', 'Sacramenti e vita della Chiesa spiegati in modo semplice', 'Verifiche con soluzioni e griglie di valutazione', 'Allineato ai Traguardi e agli OSA dell\'IRC'];
export const recognizeItems: Array<{
  icon: LucideIcon;
  title: string;
  text: string;
}> = [{
  icon: CalendarXIcon,
  title: 'La domenica va via a preparare lezioni',
  text: 'Passi parte del fine settimana a preparare le lezioni di religione della settimana successiva?'
}, {
  icon: FolderSearchIcon,
  title: 'Non sai quale attività usare',
  text: 'Cerchi continuamente schede e attività adatte all\'età e alla classe dei tuoi alunni?'
}, {
  icon: LightbulbIcon,
  title: 'Vorresti spiegazioni più chiare',
  text: 'Ti serve materiale più visivo per spiegare la Bibbia, l\'anno liturgico e i sacramenti in modo semplice?'
}, {
  icon: PencilRulerIcon,
  title: 'Vorresti smettere di creare tutto da zero',
  text: 'Vorresti che lezioni, schede e verifiche fossero già pronte?'
}, {
  icon: ShuffleIcon,
  title: 'Gli alunni hanno bisogno di varietà',
  text: 'Vorresti usare più giochi, mappe e attività pratiche durante l\'ora di religione?'
}, {
  icon: LayoutGridIcon,
  title: 'Ti serve una struttura più chiara',
  text: 'Vorresti tutto il materiale raccolto in un unico sistema, per trovare subito quello che ti serve?'
}];

export const volumes: Array<{
  number: string;
  level: string;
  classes: string;
  title: string;
  tagline: string;
  intro: string;
  items: string[];
  outro: string;
  note?: string;
  spine: string;
  rule: string;
}> = [{
  number: 'Tomo 01',
  level: 'Anno',
  classes: 'Primaria 1ª–5ª e Secondaria 1ª–3ª',
  title: 'Programmazione Annuale + 12 Lezioni Minuto per Minuto',
  tagline: 'Tutto l\'anno di IRC organizzato, classe per classe.',
  intro: 'Il primo tomo contiene la programmazione annuale per ogni classe e 12 lezioni da 60 minuti, spiegate minuto per minuto, con pagina docente e scheda alunno.',
  items: ['I 4 nuclei IRC e i Traguardi', 'Programmazione annuale Primaria 1ª–5ª', 'Programmazione annuale Secondaria 1ª–3ª', 'Piano di 12 settimane', 'Tabella di progressione', '12 lezioni minuto per minuto (60\')', 'Pagina docente + scheda alunno', 'Verifica Primaria e verifica Secondaria', 'Soluzioni (copia docente) e griglia di valutazione IRC'],
  outro: 'Allineato ai Traguardi e agli OSA dell\'IRC (Intesa DPR 11/02/2010).',
  spine: 'bg-blue-700 text-white',
  rule: 'border-white/30'
}, {
  number: 'Tomo 02',
  level: 'Bibbia',
  classes: 'Primaria e Secondaria',
  title: 'La Bibbia per Bambini e Ragazzi',
  tagline: 'I grandi racconti biblici, pronti per la tua classe.',
  intro: 'Il secondo tomo raccoglie racconti dell\'Antico e del Nuovo Testamento con schede, giochi e mappe.',
  items: ['Racconti dell\'Antico Testamento', 'Racconti del Nuovo Testamento', 'Schede da stampare', 'Giochi', 'Mappe'],
  outro: 'Materiale pronto per far conoscere la Bibbia in modo semplice e coinvolgente.',
  spine: 'bg-sky-500 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Tomo 03',
  level: 'Liturgia',
  classes: 'Primaria e Secondaria',
  title: 'Anno Liturgico e Feste',
  tagline: 'Ogni tempo dell\'anno liturgico, già preparato.',
  intro: 'Il terzo tomo accompagna la classe lungo l\'anno liturgico e le sue feste principali.',
  items: ['Avvento', 'Natale', 'Quaresima', 'Pasqua', 'Pentecoste', 'Colori liturgici', 'Calendario liturgico'],
  outro: 'Arrivi a ogni festa con lezioni e schede già pronte.',
  spine: 'bg-green-600 text-white',
  rule: 'border-white/30'
}, {
  number: 'Tomo 04',
  level: 'Sacramenti',
  classes: 'Primaria e Secondaria',
  title: 'Sacramenti e Vita della Chiesa',
  tagline: 'I sacramenti e la Chiesa spiegati con chiarezza.',
  intro: 'Il quarto tomo presenta i 7 sacramenti, la Messa spiegata passo per passo e la Chiesa con i suoi luoghi.',
  items: ['I 7 sacramenti', 'La Messa spiegata', 'La Chiesa e i suoi luoghi', 'Schede da stampare'],
  outro: 'Utile anche per i catechisti che preparano i loro incontri.',
  spine: 'bg-yellow-400 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Tomo 05',
  level: 'Santi & Arte',
  classes: 'Secondaria',
  title: 'Santi, Arte Sacra e Religioni del Mondo',
  tagline: 'Per la secondaria: santi, arte e dialogo tra religioni.',
  intro: 'Il quinto tomo, pensato per la secondaria, unisce le figure dei santi, la lettura delle opere d\'arte e le grandi religioni del mondo.',
  items: ['Santi', 'Lettura di opere d\'arte sacra', 'Ebraismo', 'Islam', 'Induismo', 'Buddhismo', 'Dialogo tra le religioni'],
  outro: 'Materiale strutturato per l\'IRC nella secondaria di primo grado.',
  spine: 'bg-orange-500 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Tomo 06',
  level: 'Verifiche',
  classes: 'Primaria e Secondaria',
  title: 'Verifiche e Valutazione',
  tagline: 'Verificare e valutare senza perdere le serate.',
  intro: 'Il sesto tomo raccoglie verifiche per classe, soluzioni, griglie e rubriche, con 100 giudizi pronti per la scheda di valutazione.',
  items: ['Verifiche A/B per classe', 'Soluzioni', 'Griglie di valutazione', 'Rubriche', '100 giudizi pronti per la scheda di valutazione'],
  outro: 'Un passo finale completo per verificare e valutare con serenità.',
  note: 'Materiale didattico indipendente: non è una pubblicazione ufficiale del Ministero dell\'Istruzione e del Merito né della CEI.',
  spine: 'bg-red-600 text-white',
  rule: 'border-white/30'
}];
export const volumesIntro = {
  title: '6 tomi digitali per Primaria e Secondaria',
  text: 'Un sistema organizzato con programmazione, lezioni pronte, schede da stampare e verifiche per tutte le classi di IRC. Apri il tomo, scegli la lezione e stampa. Meno preparazione. Più tempo per i tuoi alunni.'
};
export const packItems: Array<{
  icon: LucideIcon;
  title: string;
  text: string;
}> = [{
  icon: BookOpenTextIcon,
  title: 'Lezioni pronte',
  text: '12 lezioni minuto per minuto nel Tomo 1 e lezioni pronte in ogni tomo: niente più documento bianco.'
}, {
  icon: FileTextIcon,
  title: 'Schede da stampare',
  text: 'Schede in 3 livelli da stampare e usare subito – per il lavoro individuale, il ripasso e l\'approfondimento.'
}, {
  icon: ShapesIcon,
  title: 'Materiale visivo',
  text: 'Mappe, calendario liturgico e colori liturgici rendono i contenuti più concreti e facili da capire.'
}, {
  icon: DicesIcon,
  title: 'Giochi e attività',
  text: 'Giochi e attività pratiche per dare varietà all\'ora di religione.'
}, {
  icon: ClipboardCheckIcon,
  title: 'Verifiche con soluzioni',
  text: 'Verifiche con soluzioni (copia docente) e griglie di valutazione con giudizi IRC.'
}, {
  icon: CalendarCheckIcon,
  title: 'Programmazione annuale',
  text: 'Per ogni classe, dalla 1ª Primaria alla 3ª Secondaria, sui 4 nuclei: Dio e l\'uomo · La Bibbia e le altre fonti · Il linguaggio religioso · I valori etici e religiosi.'
}];

// Etichette nello stesso ordine di REAL_PAGE_URLS (pagine reali del Tomo 1).
const pageLabels: Array<{ label: string; title: string; text: string }> = [
  { label: 'Lezione pronta · Tomo 1', title: 'Lezione 1: Il mondo è un dono', text: 'Una lezione di 60 minuti spiegata minuto per minuto, con la pagina docente.' },
  { label: 'Scheda alunno · Tomo 1', title: 'L\'Avvento: aspettare con il cuore', text: 'Una scheda da stampare e usare subito in classe.' },
  { label: 'Programmazione · Tomo 1', title: 'Programmazione annuale', text: 'La programmazione per ogni classe, organizzata sui 4 nuclei e sui Traguardi dell\'IRC.' },
  { label: 'Verifica · Tomo 1', title: 'Verifica con soluzioni', text: 'Verifica pronta con la copia docente delle soluzioni.' },
  { label: 'Valutazione · Tomo 1', title: 'Griglia di valutazione IRC', text: 'Griglia con i giudizi NS/S/B/D/O per valutare in modo chiaro.' }
];
const pageSources: Array<{ src: string }> = REAL_PAGE_URLS.length > 0 ? REAL_PAGE_URLS.map((src: string) => ({ src })) : basePageSamples;
export const pageSamples: Array<{ src: string; label: string; title: string; text: string }> = pageSources.map((page: { src: string }, index: number) => ({
  ...pageLabels[index % pageLabels.length],
  src: page.src
}));
export const pageTypes = ['Lezioni pronte', 'Schede da stampare', 'Attività pratiche', 'Verifiche e soluzioni'];
export const bonuses: Array<{
  title: string;
  text: string;
  items: string[];
  benefit: string;
}> = [{
  title: 'Recita di Natale + Avvento Pronto',
  text: 'Tutto per il Natale a scuola: la recita e l\'Avvento già preparati.',
  items: ['Copione completo per la recita (ruoli per 20–25 bambini)', '6 canti suggeriti con testo', 'Calendario d\'Avvento con 24 mini-attività', 'Lavoretti'],
  benefit: 'Arrivi a dicembre con la recita già pronta.'
}, {
  title: 'Quaresima e Pasqua Pronte',
  text: 'Un percorso completo per accompagnare la classe verso la Pasqua.',
  items: ['Percorso di 6 settimane', 'Via Crucis per bambini', 'Attività per la Settimana Santa', 'Schede'],
  benefit: 'Sei settimane già organizzate, dalla Quaresima a Pasqua.'
}, {
  title: 'Inizio Lezione in 5 Minuti',
  text: '40 attività di apertura per partire subito con l\'ora di religione.',
  items: ['Riflessioni', 'Preghiere brevi', 'Domande stimolo', 'Giochi'],
  benefit: 'Una partenza diversa per ogni lezione.'
}, {
  title: 'Comunicazione con le Famiglie',
  text: 'Modelli pronti per comunicare con le famiglie durante tutto l\'anno.',
  items: ['Modelli di lettere', 'Avvisi e comunicazioni per eventi', 'Traccia per i colloqui', 'Presentazione dell\'IRC a inizio anno'],
  benefit: 'Basta compilare e inviare.'
}, {
  title: 'Poster per l\'Aula di Religione',
  text: '20 poster da stampare per l\'aula.',
  items: ['Anno liturgico e colori liturgici', 'Libri della Bibbia', '10 comandamenti e beatitudini', 'Regole della classe'],
  benefit: 'Un\'aula ordinata e visiva in pochi minuti.'
}];
export const BONUS_VALUE = '19 €';
export const steps: Array<{
  title: string;
  text: string;
}> = [{
  title: 'Apri RELIGIONE PRONTA™',
  text: 'Scarica il pacchetto e apri il tomo adatto alla tua classe.'
}, {
  title: 'Scegli lezione e materiale',
  text: 'Trova l\'argomento giusto e scegli le schede, le attività o le verifiche adatte ai tuoi alunni.'
}, {
  title: 'Preparati in 5 minuti',
  text: 'Leggi la pagina docente e seleziona il materiale che ti serve.'
}, {
  title: 'Stampa e insegna',
  text: 'Stampa le schede o usa il materiale in digitale – ed entra in aula con tutto pronto.'
}];
export const audienceItems = ['Sei docente di religione nella Primaria o nella Secondaria di primo grado', 'Vuoi tutto il materiale di IRC raccolto in un sistema organizzato', 'Vuoi smettere di creare ogni scheda da zero', 'Ti servono schede, mappe e attività visive', 'Vuoi usare giochi e attività pratiche', 'Vuoi verifiche, soluzioni e griglie di valutazione già pronte', 'Sei catechista e cerchi attività su Bibbia, anno liturgico e sacramenti', 'Vuoi entrare in aula più preparato'];
export const timeValueTasks = ['Cercare attività e racconti adatti', 'Scrivere schede ed esercizi', 'Preparare mappe e spiegazioni visive', 'Organizzare le feste dell\'anno liturgico', 'Preparare verifiche e giudizi'];
export const offerRows: Array<{
  label: string;
  detail: string;
}> = [{
  label: '6 tomi digitali – 240 pagine',
  detail: 'Programmazione e 12 lezioni, Bibbia, anno liturgico, sacramenti, santi e religioni, verifiche e valutazione'
}, {
  label: '5 bonus senza costi aggiuntivi',
  detail: 'Recita di Natale + Avvento, Quaresima e Pasqua, inizio lezione in 5 minuti, comunicazione con le famiglie, poster per l\'aula'
}, {
  label: 'Primaria 1ª–5ª e Secondaria 1ª–3ª',
  detail: 'Lezioni pronte, schede da stampare, verifiche con soluzioni'
}];
export const offerIncludes = ['Download digitale immediato', 'File PDF da salvare', 'Schede da stampare', 'Nessun prodotto fisico spedito'];
export const PRICE = '19 €';
export const PRICE_OLD = '97 €';

// Solo recensioni autentiche di RELIGIONE PRONTA. La sezione resta nascosta finché è vuota.
export const testimonials: Array<{
  quote: string;
  name: string;
  location: string;
}> = [];
export const faqs = [{
  q: 'Come ricevo il materiale?',
  a: 'RELIGIONE PRONTA™ è un prodotto digitale. Dopo l\'acquisto ricevi l\'accesso ai file PDF da scaricare e salvare. Puoi usarli su computer, tablet o smartphone e stampare le schede che ti servono.'
}, {
  q: 'È adatto alla scuola italiana?',
  a: 'Sì. Il materiale è pensato per l\'Insegnamento della Religione Cattolica nella scuola Primaria e nella Secondaria di primo grado, allineato ai Traguardi e agli OSA dell\'IRC (Intesa DPR 11/02/2010). È un materiale didattico indipendente, non una pubblicazione ufficiale del Ministero né della CEI.'
}, {
  q: 'Per quali classi è?',
  a: 'Per la Primaria (1ª–5ª) e la Secondaria di primo grado (1ª–3ª). Il Tomo 1 contiene la programmazione annuale per ogni classe; il Tomo 5 è pensato in particolare per la secondaria.'
}, {
  q: 'Posso usarlo anche come catechista?',
  a: 'Sì. Molti moduli – la Bibbia per bambini e ragazzi, l\'anno liturgico e le feste, i sacramenti e la vita della Chiesa – sono utili anche per gli incontri di catechismo.'
}, {
  q: 'Posso davvero preparare una lezione in 5 minuti?',
  a: 'RELIGIONE PRONTA™ è pensato per rendere la preparazione più veloce. Apri un tomo, scegli una lezione o una scheda pronta e la rivedi in circa cinque minuti. Il tempo può variare in base alla lezione, alla classe e agli adattamenti necessari.'
}, {
  q: 'Ci sono schede pronte da stampare?',
  a: 'Sì. Il materiale contiene schede da stampare con attività, esercizi, giochi e mappe.'
}, {
  q: 'Sono incluse le soluzioni?',
  a: 'Sì. Le verifiche hanno le soluzioni (copia docente) e trovi griglie di valutazione, rubriche e 100 giudizi pronti per la scheda di valutazione.'
}, {
  q: 'Devo stampare tutto?',
  a: 'No. Scegli tu quali pagine stampare e puoi usare parte del materiale in digitale.'
}, {
  q: 'I 5 bonus sono inclusi?',
  a: `Sì. Tutti e cinque i bonus sono inclusi senza costi aggiuntivi – tutto a ${PRICE}.`
}, {
  q: 'È un abbonamento?',
  a: 'No. Paghi una sola volta e ricevi l\'accesso al materiale digitale. Nessun costo ricorrente.'
}, {
  q: 'Posso salvare i file?',
  a: 'Sì. Puoi scaricare e salvare i PDF per continuare a usarli secondo le condizioni d\'uso del prodotto.'
}, {
  q: 'C\'è una garanzia?',
  a: 'Sì. RELIGIONE PRONTA™ ha una garanzia di soddisfazione di 7 giorni. Se il materiale non soddisfa le tue aspettative, puoi contattarci e chiedere il rimborso entro il periodo di garanzia, secondo le condizioni.'
}];
